import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * useLiveResource — GET a public API path with:
 *   - initial fetch
 *   - polling every `intervalMs` (default 10s)
 *   - refetch on window focus / tab visibility change
 *   - refetch on custom "grupomanga:settings-updated" event (broadcast from admin)
 *
 * Data is only re-set when JSON stringification changes to avoid needless re-renders.
 */
export function useLiveResource(path, { intervalMs = 10000, initial = null, params } = {}) {
  const [data, setData] = useState(initial);
  const [loading, setLoading] = useState(true);
  const stampRef = useRef("");

  const load = useCallback(async () => {
    try {
      const res = await api.get(path, { params });
      const stamp = JSON.stringify(res.data);
      if (stamp !== stampRef.current) {
        stampRef.current = stamp;
        setData(res.data);
      }
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, JSON.stringify(params)]);

  useEffect(() => {
    load();
    const id = setInterval(load, intervalMs);
    return () => clearInterval(id);
  }, [load, intervalMs]);

  useEffect(() => {
    const onFocus = () => load();
    const onVisibility = () => {
      if (document.visibilityState === "visible") load();
    };
    const onCustom = () => load();
    const onStorage = (e) => {
      if (e.key === "grupomanga:settings-updated") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("grupomanga:settings-updated", onCustom);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("grupomanga:settings-updated", onCustom);
      window.removeEventListener("storage", onStorage);
    };
  }, [load]);

  return { data, loading, reload: load };
}
